import { useState } from 'react';
import { ArrowLeft, ArrowRight, Factory, FileText, Loader2, Package, ShieldCheck, Sparkles } from 'lucide-react';
import Brand from './Brand';
import BusinessInformation from './BusinessInformation';
import { api } from '../services/api';

function GoogleLogo() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.39-.18-2.05H12v3.88h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.89-1.74 2.98-4.3 2.98-7.36Z" />
    <path fill="#34A853" d="M12 22c2.7 0 4.96-.89 6.62-2.41l-3.24-2.51c-.89.6-2.02.96-3.38.96-2.6 0-4.8-1.76-5.59-4.12H3.06v2.59A10 10 0 0 0 12 22Z" />
    <path fill="#FBBC05" d="M6.41 13.92a6 6 0 0 1 0-3.84V7.49H3.06a10 10 0 0 0 0 9.02l3.35-2.59Z" />
    <path fill="#EA4335" d="M12 5.96c1.47 0 2.79.51 3.82 1.51l2.86-2.86A9.6 9.6 0 0 0 12 2a10 10 0 0 0-8.94 5.49l3.35 2.59C7.2 7.72 9.4 5.96 12 5.96Z" />
  </svg>;
}

export default function AuthScreen({ session, onToast, onNavigate, signup = false }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');

  async function authenticate(kind, operation) {
    if (busy) return;
    setBusy(kind);
    setError('');
    setErrorCode('');
    try { await operation(); }
    catch (err) { setError(err.message || '로그인하지 못했습니다. 다시 시도해주세요.'); setErrorCode(err.code || ''); }
    finally { setBusy(null); }
  }

  function submit(event) {
    event.preventDefault();
    return authenticate('email', () => signup ? api.signup(email, password, name) : api.login(email, password));
  }

  return <><div className="auth-screen">
    <section className="auth-story">
      <Brand />
      <div className="auth-story-content">
        <span className="auth-eyebrow"><span className="live-dot" /> THE CONNECTED MANUFACTURING WORKSPACE</span>
        <h1>좋은 아이디어가<br />좋은 제품이 되는 곳<span>.</span></h1>
        <p>파트너를 찾고, 조건을 맞추고, 납품까지.<br />당신의 제조 비즈니스를 하나의 흐름으로 연결합니다.</p>
        <div className="auth-flow">
          <div><Factory /><strong>매칭</strong><small>맞는 파트너를 찾고</small></div><span />
          <div><FileText /><strong>견적</strong><small>투명하게 비교하고</small></div><span />
          <div><Package /><strong>납품</strong><small>일정을 함께 관리</small></div>
        </div>
        <div className="auth-quote"><span className="quote-line" /><p>복잡했던 제조 업무,<br /><strong>이제 대화하듯 자연스럽게.</strong></p><Sparkles size={35} /></div>
      </div>
      <footer>© {new Date().getFullYear()} LINKFLOW <span>Built for better collaboration.</span></footer>
      <div className="auth-orbit orbit-one" /><div className="auth-orbit orbit-two" />
    </section>
    <section className="auth-form-side">
      <button className="auth-home-link" disabled={!!busy} onClick={() => onNavigate('home')}><ArrowLeft size={15} /> 서비스 소개로 돌아가기</button>
      <div className="auth-mobile-brand"><Brand /></div>
      <div className="auth-form-wrap">
        <span className="auth-form-eyebrow">WELCOME TO LINKFLOW</span>
        <h2>{signup ? '새로운 흐름의 시작' : '다시 만나 반가워요'}</h2>
        <p>{signup ? '계정을 만들고 기업 워크스페이스를 시작하세요.' : '로그인하고 우리 기업의 업무를 이어가세요.'}</p>
        <button className="google-auth-button" disabled={!!busy} onClick={() => authenticate('google', () => api.loginWithGoogle())}>
          {busy === 'google' ? <Loader2 className="spin" size={20} /> : <GoogleLogo />}
          <span>{busy === 'google' ? 'Google 로그인 진행 중…' : 'Google로 계속하기'}</span>
        </button>
        <div className="auth-divider auth-email-divider"><span />또는 이메일로 {signup ? '가입' : '로그인'}<span /></div>
        {error && <div className="inline-error auth-login-error" role={errorCode === 'auth/popup-closed-by-user' || errorCode === 'AUTH_CANCELLED' ? 'status' : 'alert'}>{error}</div>}
        <form onSubmit={submit}>
          {signup && <label className="field"><span>이름</span><input value={name} onChange={e => setName(e.target.value)} placeholder="홍길동" required autoComplete="name" disabled={!!busy} /></label>}
          <label className="field"><span>이메일</span><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@company.com" required autoComplete="email" disabled={!!busy} /></label>
          <label className="field"><span>비밀번호</span><input type="password" value={password} onChange={e => setPassword(e.target.value)} minLength={6} placeholder="비밀번호를 입력하세요" required autoComplete={signup ? 'new-password' : 'current-password'} disabled={!!busy} /></label>
          <button className="button primary auth-submit" disabled={!!busy}>{busy === 'email' ? <Loader2 className="spin" size={18} /> : signup ? '계정 만들기' : '로그인'}{busy !== 'email' && <ArrowRight size={17} />}</button>
        </form>
        {!signup && <button className="forgot-password" disabled={!!busy} onClick={() => {
          if (!email.trim()) { setError('재설정 이메일을 입력해 주세요.'); setErrorCode(''); return; }
          authenticate('reset', async () => { await api.resetPassword(email); onToast('비밀번호 재설정 이메일을 요청했습니다. 메일함을 확인해 주세요.'); });
        }}>비밀번호를 잊으셨나요?</button>}
        <p className="auth-switch">{signup ? '이미 계정이 있으신가요?' : '처음 방문하셨나요?'}<button disabled={!!busy} onClick={() => onNavigate(signup ? 'login' : 'signup')}>{signup ? '로그인' : '회원가입'}</button></p>
        <div className="auth-divider"><span />먼저 경험해보세요<span /></div>
        <button className="demo-entry" disabled={!!busy} onClick={() => authenticate('demo', async () => { await api.enterDemo(); onNavigate('chat'); })}>
          <span className="demo-entry-icon"><Sparkles size={21} /></span><span><strong>먼저 둘러볼까요?</strong><small>가상 데이터로 모든 업무 흐름 체험하기</small></span><ArrowRight size={18} />
        </button>
        <p className="auth-safe"><ShieldCheck size={13} /> Firebase 인증으로 안전하게 로그인합니다.</p>
        {session.error && session.error !== error && <p className="inline-error" role="alert">{session.error}</p>}
      </div>
      <footer className="auth-form-footer">제조사 탐색부터 납품까지, LINKFLOW와 함께.</footer>
    </section>
  </div><footer className="public-business-footer"><BusinessInformation /></footer></>;
}
